import {z} from 'zod'
import i18n from '@/translations/i18n'
import {PaginationResponse} from '@/types'
import {ProjectByPatient} from '../projects/types'

/**
 * Member reference type for conversation creation
 * Used when creating conversations to reference users or groups
 */
export const MemberReference = z.object({
    type: z.enum(['user', 'group']),
    id: z.string().min(1)
})
export type MemberReference = z.infer<typeof MemberReference>

export const ConversationEntityType = z.enum(['project', 'treatment_request', 'medical_record_report'])
export type ConversationEntityType = z.infer<typeof ConversationEntityType>

// Member type (for users or groups in a conversation)
export const ConversationMember = z.object({
    id: z.string().min(1),
    type: z.enum(['user', 'group']),
    fullName: z.string().optional(), // For users
    name: z.string().optional() // For groups
})
export type ConversationMember = z.infer<typeof ConversationMember>

// Base conversation type
export const Conversation = z.object({
    id: z.string().min(1),
    // Null for internal conversations (no patient association)
    patientId: z.string().nullable(),
    title: z.string(),
    members: z.array(ConversationMember),
    firstMessage: z.object({
        message: z.string().min(1),
        sender: z.object({
            id: z.string().min(1),
            firstName: z.string().min(1),
            lastName: z.string().min(1)
        })
    }),
    totalMessages: z.number(),
    hasUnreadMessages: z.boolean().nullable(),
    isUrgent: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string().nullable()
})
export type Conversation = z.infer<typeof Conversation>

// List of conversations
export const ConversationsList = z.object({
    data: z.array(Conversation),
    meta: PaginationResponse
})
export type ConversationsList = z.infer<typeof ConversationsList>

// Sender type for conversation messages
export const ConversationMessageSender = z.object({
    id: z.string().min(1),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    avatarUrl: z.string().nullable()
})
export type ConversationMessageSender = z.infer<typeof ConversationMessageSender>

// Conversation message
export const ConversationMessage = z.object({
    id: z.string().min(1),
    conversationId: z.string().min(1),
    message: z.string(),
    isUrgent: z.coerce.boolean(),
    createdAt: z.string(),
    senderId: z.string().min(1),
    updatedAt: z.string().nullable(),
    sender: ConversationMessageSender
})
export type ConversationMessage = z.infer<typeof ConversationMessage>

// List of conversation messages
export const ConversationMessagesList = z.object({
    data: z.array(ConversationMessage),
    meta: PaginationResponse
})
export type ConversationMessagesList = z.infer<typeof ConversationMessagesList>

// New conversation form validation schema
export const NewConversationFormValidationSchema = z
    .object({
        title: z.string().min(1, i18n.t('errors:required')),
        memberIds: z.array(z.string()).min(1, i18n.t('errors:required')),
        message: z.string().min(1, i18n.t('errors:required')),
        isUrgent: z.boolean().default(false),
        entityType: ConversationEntityType.nullish()
    })
    .and(
        z
            .discriminatedUnion('entityType', [
                z.object({
                    entityType: z.literal('project'),
                    entity: ProjectByPatient.pick({
                        id: true,
                        code: true,
                        startDate: true,
                        expirationDate: true
                    })
                }),
                z.object({
                    entityType: z.literal('treatment_request'),
                    entity: z.object({
                        id: z.string(),
                        type: z.string()
                    })
                }),
                z.object({
                    entityType: z.literal('medical_record_report'),
                    entity: z.object({
                        id: z.string(),
                        name: z.string()
                    })
                })
            ])
            .nullish()
    )

export type NewConversationFormValidationSchema = z.infer<typeof NewConversationFormValidationSchema>
